import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  getSubgridKey,
  initTreeSubgrid,
  reinitializeSubgrid
} from "@mnjsplatform/data/lib/actions/subgridActions";
import SubgridPlaceholder from "./Subgrid/SubgridPlaceholder";
import i18next from "i18next";
import { getComboText } from "@mnjsplatform/data/lib/actions/comboActions";

const parseFieldInfo = ({ EditorMethod, Width, ...fieldInfo }) => {
  const editorMethod = EditorMethod.split(',').map(p => p.split(':'));
  let isSelectedFieldNameParam = editorMethod.find(p => p[0] === "DD_IsSelectedFieldName");
  let textFieldNameParam = editorMethod.find(p => p[0] === "DD_TextFieldName");
  let maxHeightParam = editorMethod.find(p => p[0] === "DD_MaxHeight");
  let options = editorMethod.find(p => p[0] === "OPTIONS");
  let keyFieldName = editorMethod.find(p => p[0] === "DD_SetKeyFieldName");

  const isSelectedFieldName = isSelectedFieldNameParam ? isSelectedFieldNameParam[1] : "";
  const textFieldName = textFieldNameParam ? textFieldNameParam[1] : "";
  options = options ? options[1] : "";
  keyFieldName = keyFieldName ? keyFieldName[1] : "";
  let maxHeight = maxHeightParam ? maxHeightParam[1] : "";
  maxHeight = parseInt(maxHeight);
  if (isNaN(maxHeight)) {
    maxHeight = 300;
  }

  return {isSelectedFieldName, textFieldName, options: {disableAll: options.indexOf("DISALL") >= 0, maxHeight, width: Width}, keyFieldName};
}

const SubgridTreeMultiSelectItem = ({ row, treeInfo, rowIndex, rowKey, subgridDataItem, title, isAllChecked = false, isCheckedFieldName, labelFieldName, onChange  }) => {
  const dispatch = useDispatch();
  const {IsEmpty, IsExpanded} = treeInfo;

  const changeHandler = async (e) => {
    if (rowIndex >= 0) {
      await dispatch(subgridDataItem.setField(rowIndex, isCheckedFieldName, e.target.checked));
      await dispatch(subgridDataItem.save(rowIndex));
    } else if (typeof onChange === "function") {
      await onChange();
    }
  };

  const toggleTreeNode = async (e) => {
    if (!IsEmpty) {
      await dispatch(subgridDataItem.treeOperation(rowIndex, !IsExpanded));
    }
  }

  const isChecked = row && row[isCheckedFieldName] || isAllChecked;
  const label = row && row[labelFieldName] || title || "";

  const level = treeInfo?.Level || 0;

  return (
    <div className={"d-flex dropdown-item flex-row"} style={{padding: `7px 7px 7px ${7 + (level * 35)}px`}} >
      <div>
        <i className={`zmdi zmdi-${IsExpanded ? "minus":"plus"}-square mr-1 ${IsEmpty ? "invisible" : ""}`} onClick={toggleTreeNode} />
      </div>
      <div>
        <input checked={isChecked} id={rowKey} type={"checkbox"} className={"mr-1 c-pointer"} onChange={changeHandler}/>
      </div>
      <div>
        <label className={"c-pointer"} htmlFor={rowKey}>{label}</label>
      </div>
    </div>
  );
}

const SubgridTreeMultiSelect = ({subgridDataItem, parentDataItem, rowKeyString, rowIndex, fieldInfo, rootPath, readOnly, withSubgridParams, onEndEditField}) => {

  const dispatch = useDispatch();

  const [isOpen, setOpen] = useState(false);
  const [subgridKey, setSubgridKey] = useState(null);
  const [offsetTop, setOffsetTop] = useState(0);

  const elRef = useRef();

  useEffect(() => {
    const { offsetTop } = elRef.current;
    const handleClickOutside = async (event) => {
      if (isOpen) {
        const spinnerEl = document.getElementById("spinner-container");
        if (elRef.current && !elRef.current.contains(event.target) && !spinnerEl) {
          onClose();
        }
      }
    }
    setOffsetTop(offsetTop + 32);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [elRef, isOpen]);

  useEffect(async () => {
    const key = await dispatch(initTreeSubgrid(
      parentDataItem,
      fieldInfo.Name,
      rowIndex,
      rootPath,
      readOnly,
      withSubgridParams,
      false,
      rowKeyString
    ));
    setSubgridKey(key);
  }, [rowKeyString])

  const {isSelectedFieldName, textFieldName, options} = parseFieldInfo(fieldInfo);

  const onClose = async () => {
    if (isOpen) {
      const label = await updateLabel();
      setOpen(false);
    }
  }

  const onOpen = async () => {
    if (!isOpen) {
      setOpen(true);
    }
  }

  const onToggleAllHandler = async (toSelect) => {
    const parameters = {
      ToFillSSet: toSelect ? 1 : 0
    };
    await dispatch(reinitializeSubgrid(parentDataItem, subgridKey, parameters));
    await updateLabel();
    isOpen && setOpen(false);
  }

  const clearSelection = async (e) => {
    e?.stopPropagation();
    await onToggleAllHandler(false);
  }

  const initSubgridHandler = async () => {
    if(!isOpen) {
      await onOpen();
    } else {
      await onClose();
    }
  }

  const updateLabel = async() => {
    let label;
    const res = await dispatch(getComboText(parentDataItem, rowIndex, fieldInfo.Name));
    if (res && res.label) {
      label = res.label;
      await dispatch(parentDataItem.setField(rowIndex, fieldInfo.Name, label || null));
      return label;
    }
  };

  const params = {isCheckedFieldName: isSelectedFieldName, labelFieldName: textFieldName}

  const rows = subgridDataItem?.dataTable() || [];
  const rowTreeInfo = subgridDataItem?.rawData?.RowTreeInfo;
  const isAllChecked = rows.filter(r => !!r[params.isCheckedFieldName]).length === rows.length;

  const fieldValue = parentDataItem.getField(0, fieldInfo.Name);

  return (
    <div className={"subgrid-multiselect"} ref={elRef}>

      <div className={"dropdown-btn btn -icon"} onClick={initSubgridHandler} style={{minWidth: options.width}}>
        <div className={"ellips nowrap text-left"} style={{paddingRight: "20px"}}>{fieldValue}</div>
        <i className={"zmdi zmdi-chevron-down ml-2"}/>
        <i className={"zmdi zmdi-close ml-2"} style={{right: "25px"}} onClick={clearSelection}/>
      </div>

      {isOpen && rows.length === 0 &&
        <div className={"dropdown-list-container"} style={{top: offsetTop}}>{i18next.t("GridViewEmptyLabel")}</div>
      }

      {isOpen && rows.length > 0 &&

        <div className={"dropdown-list-container"} style={{top: offsetTop, maxHeight: options.maxHeight}}>
          {!options.disableAll &&
            <SubgridTreeMultiSelectItem subgridDataItem={subgridDataItem} onChange={() => onToggleAllHandler(!isAllChecked)}
                                        title={i18next.t("All")} rowKey={`${params.labelFieldName}_ALL`}
                                        isAllChecked={isAllChecked} {...params} />
          }
          {rows.map((row, rowIndex) => {
            let rowKey = (subgridDataItem?.getRowKey(row) || "");
            rowKey = JSON.stringify(rowKey).replace(/\W/gi, '');

            return (<SubgridTreeMultiSelectItem subgridDataItem={subgridDataItem} rowIndex={rowIndex}
                                            row={row} key={rowKey} rowKey={rowKey} treeInfo={rowTreeInfo[rowIndex]} {...params} />)
          })}
        </div>
      }
    </div>
  );

}

const SubgridTreeMultiSelectWrapper = ({parentDataItem, rowIndex, fieldInfo, rootPath, readOnly, withSubgridParams, onEndEditField}) => {

  const {keyFieldName} = parseFieldInfo(fieldInfo);

  const rowKeyString = parentDataItem.getField(rowIndex, keyFieldName);

  const subgridKey = getSubgridKey(parentDataItem, rowIndex, fieldInfo.Name, rootPath, rowKeyString);
  return (
    <SubgridPlaceholder subgridKey={subgridKey} alwaysRenderChildren={true}>
      {(subgridItem, subgridItemParams) =>
        <SubgridTreeMultiSelect {...{subgridDataItem: subgridItem, rowKeyString, parentDataItem, rowIndex, fieldInfo, rootPath, readOnly, withSubgridParams, onEndEditField}} />
      }
    </SubgridPlaceholder>
  )
}

export default SubgridTreeMultiSelectWrapper;